import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useLazyQuery } from '@apollo/react-hooks';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { getFiltersProp } from '../../../../../../../redux/selectors/CurrentUserSelectors';
import {
	DEPRECATED_STATUS_EDITOR_REDO,
	DEPRECATED_STATUS_RE_EDIT_REVIEW,
	PHOTOSHOOT_TYPE_SERVICE,
	STATUS_EDITOR_UPLOAD,
	STATUS_FINDING_EDIT_TECHNICIAN,
	STATUS_LIVE,
	STATUS_PROOFING
} from '../../../../../../../utils/Config/Constants';
import { getPaginatedTableVariablesByTypeAndSearchTypeScript } from '../../../../../../../components/PL/PLTable/TypeScriptHelpers';
import { FINANCE_TABLE_TYPE } from '../../../../../../../components/PL/PLTable/config';
import { GET_FINANCE_SERVICES_WITH_QUERY } from '../../queries';
import { updateFilter } from '../../../../../../../redux/store/filters/actions';
import { getPhotographerPayFromOptions, getStripeAccountUrl } from '../../../../../../../utils/Helpers/PaymentHelper/PaymentHelper';
import { PLFILTER_TYPE_FINANCE } from '../../../../../../../components/PL/PLFilters/config';
import PLFilters from '../../../../../../../components/PL/PLFilters';
import { UI_LABELED_INPUT_LINK_TYPE } from '../../../../../../../components/UI/UILabeledInput/config';
import PLTable from '../../../../../../../components/PL/PLTable/PLTable';
import UIIconButton from '../../../../../../../components/UI/UIIconButton';
import UILabeledInput from '../../../../../../../components/UI/UILabeledInput';
import EmptyDataSet from '../../../../../../../components/PL/EmptyDataSet/EmptyDataSet';
import { AdminFinanceDownload } from '../AdminFinanceDownload';

export default function AdminFinancesTechnicianPayment({}: AdminFinancesTechnicianPaymentProps) {
	const QUERY_LIMIT = 10000;

	const [isCollapsed, setIsCollapsed] = useState<object>({});
	const [servicesInRange, setServicesInRange] = useState<ServiceType[]>([]);

	const history = useHistory();
	const filters = useSelector(getFiltersProp);
	const location = useLocation();
	const dispatch = useDispatch();

	let props = {
		filters,
		location
	};

	let baseVariables: any = {
		query: {
			// regionId: { op_eq: this.props.user.selectedRegion.id },
			type: { op_eq: PHOTOSHOOT_TYPE_SERVICE },
			status: {
				op_or: [
					//If photographer has uploaded
					{ op_eq: STATUS_LIVE },
					{ op_eq: STATUS_PROOFING },
					{ op_eq: STATUS_EDITOR_UPLOAD },
					{ op_eq: STATUS_FINDING_EDIT_TECHNICIAN },
					{ op_eq: DEPRECATED_STATUS_EDITOR_REDO },
					{ op_eq: DEPRECATED_STATUS_RE_EDIT_REVIEW }
				]
			}
		}
	};

	let variables = getPaginatedTableVariablesByTypeAndSearchTypeScript(QUERY_LIMIT, FINANCE_TABLE_TYPE, props, baseVariables);
	const [getServices, { data: servicesData }] = useLazyQuery(GET_FINANCE_SERVICES_WITH_QUERY, {
		variables
	});

	const matchingPath = location.pathname;

	const updateFilterData = async () => {
		const currentDate = moment();
		const startOfMonth = moment().startOf('month');
		const startOfMonthPlusTwo = moment().startOf('month').add(2, 'weeks');
		if (currentDate.date() >= 15) {
			dispatch(updateFilter({ route: matchingPath, filter: { name: 'startDate', value: startOfMonthPlusTwo.add(1, 'day') } }));
			dispatch(updateFilter({ route: matchingPath, filter: { name: 'endDate', value: currentDate.endOf('month') } }));
		} else {
			dispatch(updateFilter({ route: matchingPath, filter: { name: 'startDate', value: startOfMonth } }));
			dispatch(updateFilter({ route: matchingPath, filter: { name: 'endDate', value: startOfMonthPlusTwo } }));
		}
	};

	useEffect(() => {
		(async () => {
			await updateFilterData();
			await getServices();
		})();
	}, []);

	useEffect(() => {
		(() => {
			if (servicesData) {
				const fetchedServices: ServiceType[] = servicesData['services'];
				setServicesInRange(fetchedServices);
			}
		})();
	}, [servicesData]);

	const getPhotographerTotals = () => {
		let photographersTotals = {};
		let servicesWithoutPhotographer: ServiceType[] = [];
		for (let i = 0; i < servicesInRange.length; i++) {
			let service = servicesInRange[i];
			if (service.photographer) {
				let photographerServicePay = parseFloat(getPhotographerPayFromOptions(service, service.photographer));
				//Photographer already added to the totals object
				if (photographersTotals[service.photographer.id]) {
					let photographerTotalObject = photographersTotals[service.photographer.id];
					photographerTotalObject.total = photographerTotalObject.total + photographerServicePay;

					let photographerServices = photographerTotalObject.services;
					photographerServices.push({ service, pay: photographerServicePay });
					photographerTotalObject.services = photographerServices;

					photographersTotals[service.photographer.id] = photographerTotalObject;
				} else {
					photographersTotals[service.photographer.id] = {
						total: photographerServicePay,
						photographer: service.photographer,
						services: [{ service, pay: photographerServicePay }]
					};
				}
			} else {
				//No photographer
				servicesWithoutPhotographer.push(service);
			}
		}
		return { withPhotographers: photographersTotals, withoutPhotographers: servicesWithoutPhotographer };
	};

	const renderFinanceHeader = (totalServices, photographerPayTotal) => {
		return (
			<div className='paymentHeader'>
				<div className='paymentHeader__content'>
					<PLFilters type={PLFILTER_TYPE_FINANCE} updateTable={getServices} />
					<div className='paymentHeader__content--items'>total services: {totalServices}</div>
					<div className='paymentHeader__content--items'>total technician pay: ${photographerPayTotal}</div>
					<AdminFinanceDownload name={'technician-pay.csv'} json={servicesInRange} />
				</div>
			</div>
		);
	};

	const setCollapsableState = (key) => {
		if (isCollapsed[key] === true) {
			setIsCollapsed({ ...isCollapsed, [key]: false });
		} else {
			setIsCollapsed({ ...isCollapsed, [key]: true });
		}
	};

	const getCollapseClass = (key) => {
		if (isCollapsed[key] === true) {
			return 'collapse--active';
		} else {
			return 'collapse';
		}
	};

	const renderTabContent = () => {
		let totals = getPhotographerTotals();
		let photographerTotals = totals.withPhotographers;
		let servicesWithoutPhotographersArray = totals.withoutPhotographers;

		let totalsArray = Object.keys(photographerTotals).map((k) => photographerTotals[k]);
		let servicesWithoutPhotographers = servicesWithoutPhotographersArray.length > 0 && (
			<div style={{ marginTop: '32px' }}>
				<p className='admin-payment-services-label'>Services without photographer:</p>
				{servicesWithoutPhotographersArray.map((service) => (
					<div key={service.id} className='admin-payment-individual-service-container' onClick={() => history.push('service/' + service.id)}>
						<p className='admin-payment-individual-service-address'>{service.property ? service.property.address : 'Error no property'}</p>
					</div>
				))}
			</div>
		);
		let photographerPayments =
			totalsArray.length > 0 ? (
				totalsArray.map((photographerPay) => {
					return (
						<div style={{ marginTop: '32px' }} key={photographerPay.services[0].service.id}>
							<p className='admin-payment-photographer-name'>
								<span className='admin-payment-total-pay'>${photographerPay.total} </span> {photographerPay.photographer.name}
							</p>
							<UILabeledInput
								containerStyle={{ marginTop: 4, marginBottom: 16 }}
								linkUrl={getStripeAccountUrl(photographerPay.photographer.stripeAccountId)}
								type={UI_LABELED_INPUT_LINK_TYPE}
								label={'STRIPE ACCOUNT ID'}
								value={photographerPay.photographer.stripeAccountId ? photographerPay.photographer.stripeAccountId : 'Not found, notify tech team.'}
							/>
							<UIIconButton
								dark
								text={isCollapsed[photographerPay.photographer.id] ? `hide table` : `show table`}
								onClick={() => setCollapsableState(photographerPay.photographer.id)}
							/>
							{photographerPay.services && (
								<div className={getCollapseClass(photographerPay.photographer.id)}>
									<PLTable
										type={FINANCE_TABLE_TYPE}
										data={photographerPay.services.map((serviceWithPrice) => serviceWithPrice.service)}
										limit={QUERY_LIMIT}
										updateTable={getServices}
									/>
								</div>
							)}
						</div>
					);
				})
			) : (
				<EmptyDataSet message={'There are no services found...'} />
			);
		return (
			<div>
				{servicesWithoutPhotographers}
				{photographerPayments}
			</div>
		);
	};

	let totals = getPhotographerTotals();
	let photographerTotals = totals.withPhotographers;

	let totalsArray = Object.keys(photographerTotals).map((k) => photographerTotals[k]);
	//Get total number of services in range
	let totalServices = 0;
	for (let index = 0; index < totalsArray.length; index++) {
		totalServices += totalsArray[index].services.length;
	}

	let photographerPayTotal = totalsArray.reduce((acc, total) => acc + total.total, 0);
	return (
		<div>
			{renderFinanceHeader(totalServices, photographerPayTotal)}
			<div className='paymentContent'>{renderTabContent()}</div>
		</div>
	);
}
